<template>
  <ion-modal :is-open="is_open" @willDismiss="is_open = false">
    <ion-header>
      <ion-toolbar>
        <ion-title>
          Sélectionner un travail sous-traité
        </ion-title>
        <ion-buttons slot="end">
          <ion-button @click="is_open = false">
            Fermer
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
      <ion-toolbar>
        <ion-searchbar
          v-model="search"
          placeholder="Rechercher..."
        ></ion-searchbar>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <ion-list v-if="!first_loading && outsourcedWorks.length">
        <ion-item
          v-for="outsourcedWork in outsourcedWorks"
          :key="outsourcedWork.uuid"
          :button="true"
          @click="selectOutsourcedWork(outsourcedWork)"
        >
          <ion-label>
            {{ outsourcedWork.name }}
            <span v-if="outsourcedWork.reference" :class="$style['reference']">
              - {{ outsourcedWork.reference }}
            </span>
            <span v-if="outsourcedWork.contractor_name" :class="$style['contractor']">
              ({{ outsourcedWork.contractor_name }})
            </span>
          </ion-label>
        </ion-item>

        <ion-infinite-scroll
          :hidden="has_more_data || loading"
          threshold="200px"
          @ionInfinite="loadOutsourcedWorks($event)"
        >
          <ion-infinite-scroll-content></ion-infinite-scroll-content>
        </ion-infinite-scroll>
      </ion-list>

      <div
        v-else-if="!loading"
        :class="{
          'ion-padding': true,
          [$style['no-result']]: true,
        }"
      >
        <p>
          Aucun résultat trouvé
        </p>
      </div>
      <div
        v-else-if="loading"
        :class="{
          'ion-padding': true,
          [$style['no-result']]: true,
        }"
      >
        <ion-spinner></ion-spinner>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonList,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/vue";
import { OutsourcedWork } from "@/outsourced-works";

export default defineComponent({
  props: ["active", "filter"],
  emits: ["close", "change"],
  data() {
    return {
      outsourcedWorks: [] as OutsourcedWork[],
      search: "",
      first_loading: true,
      loading: false,
      page: 1,
      has_more_data: true,
      search_timeout: null as null | number,
    };
  },
  methods: {
    loadOutsourcedWorks(infinite_event?: InfiniteScrollCustomEvent) {
      if (this.loading) return;

      this.loading = true;
      this.axios
        .get("outsourced-works" + "?bypass_organization=1&page=" + this.page + "&" + this.filter_string)
        .then((response) => {
          this.outsourcedWorks = [...this.outsourcedWorks, ...response.data.data];
          this.loading = false;
          this.first_loading = false;
          this.page++;
          this.has_more_data = !!response.data.length;

          if (infinite_event) {
            infinite_event.target.complete();
          }
        });
    },
    selectOutsourcedWork(outsourcedWork: OutsourcedWork) {
      this.$emit("change", { value: outsourcedWork });
      this.is_open = false;
    },
  },
  computed: {
    is_open: {
      get() {
        return this.$props.active;
      },
      set(value: boolean) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
    filter_string() {
      const filter = {
        search: this.search ?? null,
      };

      return Object.entries(filter)
        .map(([key, value]) => {
          if (!value) return "";

          return key + "=" + encodeURIComponent(value as string);
        })
        .join("&");
    },
  }, 
  watch: {
    search() {
      if (this.search_timeout) {
        clearTimeout(this.search_timeout);
      }
      this.search_timeout = setTimeout(() => {
        this.outsourcedWorks = [];
        this.page = 1;
        this.has_more_data = true;
        this.loadOutsourcedWorks();
      }, 500);
    },
    is_open() {
      if (this.is_open) {
        this.loadOutsourcedWorks();
      }
    }
  },
  components: {
    IonContent,
    IonHeader,
    IonItem,
    IonList,
    IonSearchbar,
    IonModal,
    IonSpinner,
    IonTitle,
    IonButton,
    IonButtons,
    IonToolbar,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
  },
});
</script>

<style lang="scss" module>
.no-result {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 70%;

  > * {
    margin: 0.5em 0;
  }
}

.reference {
  color: var(--ion-color-medium);
  font-size: 0.9em;
}

.contractor {
  color: var(--ion-color-medium);
  font-size: 0.9em;
  font-style: italic;
}
</style> 